import { useState } from 'react';
import { ApiError } from '../api/client';
import { useAddOrgContact } from '../api/queries/organizations';
import { Modal } from './Modal';
import { PersonPicker } from './PersonPicker';
import { AddIndividualModal } from './AddIndividualModal';
import '../pages/NewClientPage.css';
import { Toggle } from './Toggle';

interface AddOrgContactModalProps {
  open: boolean;
  onClose: () => void;
  orgId: number;
  orgName: string;
  /** Person ids already on this company (hidden from picker). */
  existingPersonIds: number[];
  /** Called after a successful link or create+link. */
  onLinked?: () => void;
}

type Mode = 'existing' | 'new';

/**
 * Company page "Add contact": either pick an existing directory individual
 * or create a new one with the full Add Individual form, then link them
 * as a contact on this organization.
 */
export function AddOrgContactModal({
  open,
  onClose,
  orgId,
  orgName,
  existingPersonIds,
  onLinked,
}: AddOrgContactModalProps): JSX.Element {
  const addContact = useAddOrgContact(orgId);
  const [mode, setMode] = useState<Mode>('existing');
  const [personId, setPersonId] = useState<number | null>(null);
  const [jobTitle, setJobTitle] = useState('');
  const [isPrimary, setIsPrimary] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const busy = addContact.isPending;

  const reset = () => {
    setMode('existing');
    setPersonId(null);
    setJobTitle('');
    setIsPrimary(false);
    setError(null);
  };

  const close = () => {
    if (busy) return;
    reset();
    onClose();
  };

  const onLinkExisting = async () => {
    setError(null);
    if (personId === null) {
      setError('Pick an individual from the directory.');
      return;
    }
    try {
      await addContact.mutateAsync({
        person_id: personId,
        title: jobTitle.trim() || undefined,
        is_primary: isPrimary,
      });
      reset();
      onLinked?.();
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not link the contact.');
    }
  };

  // New-person path: full Add Individual modal, locked to this company as Contact.
  // Cancel returns to "Select existing" without closing the outer flow.
  if (mode === 'new' && open) {
    return (
      <AddIndividualModal
        open
        title={`Add individual as contact — ${orgName}`}
        presetContactOrgId={orgId}
        submitLabel="Create & link"
        onClose={() => setMode('existing')}
        onCreated={() => {
          onLinked?.();
          reset();
          onClose();
        }}
      />
    );
  }

  return (
    <Modal title={`Add Contact — ${orgName}`} open={open} onClose={close} busy={busy}>
      <div className="new-client-form" style={{ padding: 0 }}>
        <div className="new-client-row" style={{ marginBottom: 'var(--space-4)' }}>
          <button
            type="button"
            className={mode === 'existing' ? 'btn-cyan-primary' : 'btn-charcoal-secondary'}
            onClick={() => { setMode('existing'); setError(null); }}
            disabled={busy}
          >
            Select existing
          </button>
          <button
            type="button"
            className={mode === 'new' ? 'btn-cyan-primary' : 'btn-charcoal-secondary'}
            onClick={() => { setMode('new'); setError(null); }}
            disabled={busy}
          >
            Add new individual
          </button>
        </div>

        <div className="new-client-field new-client-field-full">
          <span className="new-client-section-label">Individual *</span>
          <PersonPicker
            value={personId}
            onChange={(id) => setPersonId(id)}
            excludePersonIds={existingPersonIds}
            disabled={busy}
            placeholder="Search directory individuals…"
          />
        </div>

        <label className="new-client-field new-client-field-full">
          <span>Title at this company</span>
          <input
            className="form-input"
            value={jobTitle}
            onChange={(e) => setJobTitle(e.target.value)}
            maxLength={120}
            placeholder="e.g. General Counsel"
            disabled={busy}
          />
        </label>

        <Toggle
          checked={isPrimary}
          onChange={setIsPrimary}
          label="Primary contact"
          disabled={busy}
          className="new-client-field"
        />

        {error && <p className="new-client-error" role="alert">{error}</p>}

        <div className="new-client-actions">
          <button type="button" className="btn-charcoal-secondary" onClick={close} disabled={busy}>
            Cancel
          </button>
          <button
            type="button"
            className="btn-cyan-primary"
            onClick={() => void onLinkExisting()}
            disabled={busy || personId === null}
          >
            {busy ? 'Linking…' : 'Link contact'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
